import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { useQuery } from '@tanstack/react-query';
import { Heading } from '@restworld/ui-ds-base';
import { cn } from '@restworld/utils-common';
import { useEmployersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';

/**
 * Single credit balance of the organization, summing every source: referral
 * gifts, CSM grants and purchased credit packs.
 *
 * No expiry date is shown on purpose: `expires_at` is serialized only on the Lab
 * credits endpoint (rw_member only), while `getReferralLink` returns totals and
 * balance alone. It is a missing field upstream, not a forgotten one here.
 *
 * The `['referral-link']` query key is shared with the dashboard referral card,
 * the invite modal, the sticky CTA and the suggested venues, so those screens read
 * the same cached balance instead of fetching twice. That only holds because all
 * five unwrap the AxiosResponse INSIDE their queryFn: a consumer that returns the
 * response and unwraps with `select` would cache the wrapper under the same key,
 * `data.credits` would read `undefined` here and this block would silently vanish
 * for the whole 60s staleTime. Any new consumer must return `response.data`.
 */
const AvailableCredits: React.FunctionComponent = () => {
  const { t } = useTranslation('payment-and-invoices');
  const { user } = useAuth();
  const employersService = useEmployersService();

  const { data } = useQuery({
    queryKey: ['referral-link'],
    queryFn: async () => {
      const response = await employersService.getReferralLink();
      return response.data;
    },
    enabled: !!user?.using_ppu_business_model,
  });

  const availableCredits = data?.credits?.available_credits ?? 0;

  // A customer who never had credits should not see a zero line among their payments.
  if (availableCredits <= 0) {
    return null;
  }

  return (
    <SectionWrapper>
      <HeaderWrapper>
        <Heading size={4} weight="semibold" color="neutral">
          {t('available_credits_title')}
        </Heading>
      </HeaderWrapper>
      <ContentWrapper>
        <Heading size={5} weight="semibold" color="neutral">
          {t('available_credits_count', { count: availableCredits })}
        </Heading>
      </ContentWrapper>
    </SectionWrapper>
  );
};

export default AvailableCredits;

const SectionWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex-col justify-start items-center gap-4 inline-flex w-full', className)} {...props} />
);

const HeaderWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('self-stretch h-10 justify-between items-center inline-flex', className)} {...props} />
);

const ContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'self-stretch p-5 rounded-2xl border border-neutral-40 flex-col justify-start items-start gap-10 flex',
      className
    )}
    {...props}
  />
);
